import { useContext } from 'react';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@sixui/ui';
import { listItemTokens } from '@sixui/ui/components/ListItem/ListItem.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { outlineTokens } from '@sixui/ui/themes/base/outline.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { MultiSessionsContext, useAllCredentialsQuery } from '~/lib/auth';
import { SidebarUserMenu } from './SidebarUserMenu';

export type ISidebarUserItemProps = IContainerProps;

const styles = stylex.create({
  host: {
    position: 'sticky',
    bottom: 0,
  },
  button: {
    flexGrow: 1,
    borderRadius: shapeTokens.corner$none,
    [listItemTokens.containerShape]: shapeTokens.corner$none,
  },
  buttonContainer: {
    borderTopColor: colorSchemeTokens.outlineVariant,
    borderTopWidth: outlineTokens.width$xs,
    borderTopStyle: 'solid',
    display: 'flex',
    height: 72,
  },
  impersonated: {
    borderTopColor: colorSchemeTokens.outlineVariant,
    borderTopWidth: outlineTokens.width$xs,
    borderTopStyle: 'solid',
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: colorSchemeTokens.errorContainer,
    color: colorSchemeTokens.onErrorContainer,
  },
  icon: {
    marginRight: '0.5rem',
  },
});

export const SidebarUserItem: React.FC<ISidebarUserItemProps> = (props) => {
  const { sx } = props;
  const userQuery = useUserQuery({});
  const userInfo = userQuery.data ? parseUserInfo(userQuery.data) : undefined;
  const { t } = useTranslation();

  const allCredentialsQuery = useAllCredentialsQuery();
  const multiSessionsContext = useContext(MultiSessionsContext);
  const currentSessionId = multiSessionsContext.currentId;
  const credentials = currentSessionId
    ? allCredentialsQuery.data?.find(
        (credentials) => credentials.sessionId === currentSessionId,
      )
    : undefined;
  const isImpersonated = credentials?.isImpersonated;

  return (
    <div {...stylex.props(styles.host, sx)}>
      {isImpersonated ? (
        <div {...stylex.props(styles.impersonated)}>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            {...stylex.props(styles.icon)}
          />
          {t('SidebarUserItem.impersonated')}
        </div>
      ) : null}

      <div {...stylex.props(styles.buttonContainer)}>
        <Skeleton loaded={userQuery.isSuccess} sx={styles.button}>
          <SidebarUserMenu
            sx={styles.button}
            user={userQuery.data}
            name={userInfo?.fullName}
            email={userInfo?.emailAddress}
            isSuperAdmin={userQuery.data?.superAdmin}
          />
        </Skeleton>
      </div>
    </div>
  );
};
